import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Island } from './Island';
import { PERSONA_ORDER, PERSONAS } from '../ai/personas';

export interface SkySceneCallbacks {
  onPolycount?: (n: number) => void;
}

export class SkyScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly controls: OrbitControls;
  readonly islands: Island[] = [];
  private readonly seaUniforms = { uTime: { value: 0 } };
  private readonly clock = new THREE.Clock();
  private rafId: number | null = null;
  private updateCallbacks: Array<(t: number, dt: number) => void> = [];
  private cloudGroup: THREE.Group | null = null;
  private cloudPhases: number[] = [];

  constructor(host: HTMLElement, cb: SkySceneCallbacks = {}) {
    this.camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 400);
    this.camera.position.set(0, 7, 22);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    host.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.enablePan = false;
    this.controls.enableZoom = false;
    this.controls.enableRotate = false;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.45;
    this.controls.minPolarAngle = Math.PI * 0.18;
    this.controls.maxPolarAngle = Math.PI * 0.5;
    this.controls.target.set(0, 2, 0);

    this.buildSky();
    this.buildLights();
    this.buildSea();
    this.buildIslands();
    this.buildClouds();

    window.addEventListener('resize', this.handleResize);

    if (cb.onPolycount) cb.onPolycount(this.estimatePolycount());
  }

  private buildSky(): void {
    const geo = new THREE.SphereGeometry(180, 16, 12);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      uniforms: {
        uTop: { value: new THREE.Color(0x12224a) },
        uMid: { value: new THREE.Color(0xff8a5e) },
        uBot: { value: new THREE.Color(0xfde0c0) }
      },
      vertexShader: `
        varying vec3 vWorldPos;
        void main(){
          vec4 wp = modelMatrix * vec4(position,1.0);
          vWorldPos = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: `
        varying vec3 vWorldPos;
        uniform vec3 uTop;
        uniform vec3 uMid;
        uniform vec3 uBot;
        void main(){
          float h = clamp((vWorldPos.y + 30.0) / 150.0, 0.0, 1.0);
          vec3 c = mix(uBot, uMid, smoothstep(0.0, 0.35, h));
          c = mix(c, uTop, smoothstep(0.45, 0.95, h));
          gl_FragColor = vec4(c, 1.0);
        }
      `
    });
    const sky = new THREE.Mesh(geo, mat);
    this.scene.add(sky);
  }

  private buildLights(): void {
    const ambient = new THREE.HemisphereLight(0xfff1d6, 0x2b3a66, 0.55);
    const sun = new THREE.DirectionalLight(0xffe0b0, 1.2);
    sun.position.set(10, 14, 6);
    const fill = new THREE.DirectionalLight(0x88a8ff, 0.4);
    fill.position.set(-8, 6, -10);
    this.scene.add(ambient, sun, fill);
  }

  private buildSea(): void {
    const geo = new THREE.PlaneGeometry(200, 200, 24, 24);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        ...this.seaUniforms,
        uColorDeep: { value: new THREE.Color(0x4a7ec2) },
        uColorShallow: { value: new THREE.Color(0xc8e5ff) }
      },
      vertexShader: `
        uniform float uTime;
        varying float vH;
        void main(){
          vec3 p = position;
          float w = sin(p.x*0.18 + uTime*1.1) * 0.6
                  + cos(p.z*0.22 + uTime*0.8) * 0.5
                  + sin((p.x+p.z)*0.07 + uTime*0.4) * 0.4;
          p.y += w;
          vH = w;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }
      `,
      fragmentShader: `
        varying float vH;
        uniform vec3 uColorDeep;
        uniform vec3 uColorShallow;
        void main(){
          float k = smoothstep(-1.2, 1.2, vH);
          vec3 c = mix(uColorDeep, uColorShallow, k);
          gl_FragColor = vec4(c, 1.0);
        }
      `
    });
    const sea = new THREE.Mesh(geo, mat);
    sea.position.y = -2.6;
    this.scene.add(sea);
  }

  private buildIslands(): void {
    const radius = 9;
    PERSONA_ORDER.forEach((pid, i) => {
      const angle = (i / PERSONA_ORDER.length) * Math.PI * 2 + Math.PI;
      const y = Math.sin(i * 1.31) * 0.8 + 2.2;
      const pos = new THREE.Vector3(Math.cos(angle) * radius, y, Math.sin(angle) * radius - 4);
      const island = new Island({ persona: PERSONAS[pid], position: pos });
      this.islands.push(island);
      this.scene.add(island.group);
    });
  }

  private buildClouds(): void {
    const cloudGeo = new THREE.IcosahedronGeometry(1.4, 0);
    const cloudMat = new THREE.MeshStandardMaterial({ color: 0xffffff, flatShading: true, transparent: true, opacity: 0.85, roughness: 1 });
    const group = new THREE.Group();
    for (let i = 0; i < 8; i++) {
      const m = new THREE.Mesh(cloudGeo, cloudMat);
      const r = 18 + Math.random() * 10;
      const a = Math.random() * Math.PI * 2;
      const baseY = -1.5 + Math.random() * 1.5;
      m.position.set(Math.cos(a) * r, baseY, Math.sin(a) * r);
      m.scale.set(1 + Math.random() * 0.6, 0.5 + Math.random() * 0.3, 1 + Math.random() * 0.6);
      m.userData.parallaxDepth = r;
      m.userData.driftSpeed = 0.02 + Math.random() * 0.04;
      m.userData.baseY = baseY;
      group.add(m);
      this.cloudPhases.push(Math.random() * Math.PI * 2);
    }
    this.cloudGroup = group;
    this.scene.add(group);
  }

  private animateClouds(t: number): void {
    if (!this.cloudGroup) return;
    this.cloudGroup.children.forEach((child, idx) => {
      const m = child as THREE.Mesh;
      const depth = (m.userData.parallaxDepth as number) ?? 18;
      const speed = (m.userData.driftSpeed as number) ?? 0.03;
      const phase = this.cloudPhases[idx] ?? 0;
      const drift = (t * speed + phase) % (Math.PI * 2);
      const baseY = (m.userData.baseY as number) ?? 0;
      m.position.x = Math.cos(drift) * depth;
      m.position.z = Math.sin(drift) * depth;
      m.position.y = baseY + Math.sin(t * 0.6 + phase) * 0.25;
    });
  }

  estimatePolycount(): number {
    let n = 0;
    this.scene.traverse(obj => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh && mesh.geometry) {
        const idx = mesh.geometry.getIndex();
        if (idx) n += idx.count / 3;
        else if (mesh.geometry.getAttribute('position')) n += mesh.geometry.getAttribute('position').count / 3;
      }
    });
    return Math.round(n);
  }

  pickIsland(point: THREE.Vector3, threshold = 2.4): Island | null {
    let best: Island | null = null;
    let bestD = threshold * threshold;
    for (const island of this.islands) {
      const d2 = island.group.position.distanceToSquared(point);
      if (d2 < bestD) {
        bestD = d2;
        best = island;
      }
    }
    return best;
  }

  onUpdate(cb: (t: number, dt: number) => void): void {
    this.updateCallbacks.push(cb);
  }

  start(): void {
    if (this.rafId !== null) return;
    const tick = () => {
      const dt = this.clock.getDelta();
      const t = this.clock.elapsedTime;
      this.seaUniforms.uTime.value = t;
      for (const island of this.islands) island.update(t, dt);
      this.animateClouds(t);
      for (const cb of this.updateCallbacks) cb(t, dt);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private handleResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };
}
